import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  ArrowDownUp,
  Eye,
  EyeOff,
  ImagePlus,
  KeyRound,
  Package,
  Pencil,
  PlusCircle,
  RefreshCcw,
  Save,
  Search,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  UploadCloud,
  XCircle,
} from "lucide-react";
import { ordersService } from "../services/orders";
import { productsService, STOCK_STATUS_OPTIONS } from "../services/products";
import { uploadImageToCloudinary } from "../services/cloudinary";
import { formatPrice } from "../utils/format";
import { optimizeImageUrl } from "../utils/images";

const PAGE_SIZE = 8;

const EMPTY_PRODUCT_FORM = {
  name: "",
  price: "",
  category: "",
  description: "",
  image_url: "",
  featured: false,
  stock_status: "in_stock",
};

const ORDER_STATUS_FILTERS = [
  { value: "all", label: "All Orders" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Completed" },
  { value: "failed", label: "Cancelled" },
];

const PRODUCT_SORT_OPTIONS = [
  { value: "updated_desc", label: "Recently Updated" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "price_asc", label: "Price Low-High" },
  { value: "price_desc", label: "Price High-Low" },
];

const STOCK_BADGE_CLASSES = {
  in_stock: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  out_of_stock: "bg-amber-50 text-amber-700 border border-amber-100",
  sold_out: "bg-rose-50 text-rose-700 border border-rose-100",
};

const ORDER_BADGE_CLASSES = {
  pending: "bg-amber-50 text-amber-700 border border-amber-100",
  paid: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  failed: "bg-rose-50 text-rose-700 border border-rose-100",
};

function formatDate(value) {
  if (!value) return "N/A";
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function normalizeProductForm(product) {
  return {
    name: product.name || "",
    price: String(product.price ?? ""),
    category: product.category || "",
    description: product.description || "",
    image_url: product.image_url || "",
    featured: Boolean(product.featured),
    stock_status: product.stock_status || "in_stock",
  };
}

function getStockLabel(value) {
  return (STOCK_STATUS_OPTIONS.find((option) => option.value === value) || STOCK_STATUS_OPTIONS[0]).label;
}

function getOrderLabel(value) {
  return (ORDER_STATUS_FILTERS.find((option) => option.value === value) || { label: value }).label;
}

function buildOrderSearchText(order) {
  const itemsText = Array.isArray(order.items) ? order.items.map((item) => item.name).join(" ") : "";
  return [
    order.customer_name,
    order.email,
    order.phone,
    order.address,
    order.payment_reference,
    itemsText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export default function AdminOrders() {
  const [accessCode, setAccessCode] = useState("");
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [editingProductId, setEditingProductId] = useState("");
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productSort, setProductSort] = useState("updated_desc");
  const [orderPage, setOrderPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const dragDepthRef = useRef(0);
  const hasNotifiedNewOrderRef = useRef(false);
  const previousOrderIdsRef = useRef([]);

  const activeAccessCode = accessCode.trim();

  const loadDashboard = async (event) => {
    if (event) event.preventDefault();

    if (!activeAccessCode) {
      toast.error("Enter the admin access code.");
      return;
    }

    setLoading(true);
    try {
      const [nextOrders, nextProducts] = await Promise.all([
        ordersService.fetchAdminOrders(activeAccessCode),
        productsService.fetchAdminProducts(activeAccessCode),
      ]);

      setOrders(nextOrders);
      setProducts(nextProducts);
      setLoaded(true);
      previousOrderIdsRef.current = nextOrders.map((order) => order.id);
      hasNotifiedNewOrderRef.current = true;

      toast.success("Dashboard loaded.");
    } catch (error) {
      console.error("Failed to load admin dashboard:", error);
      toast.error(error.message || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOrderPage(1);
  }, [orderSearch, orderStatusFilter]);

  useEffect(() => {
    setProductPage(1);
  }, [productSearch, productStatusFilter, productSort]);

  useEffect(() => {
    if (!loaded || !activeAccessCode) return undefined;

    const ordersChannel = ordersService.subscribeToOrders(async () => {
      try {
        const nextOrders = await ordersService.fetchAdminOrders(activeAccessCode);
        const previousIds = previousOrderIdsRef.current;
        const newOrders = nextOrders.filter((order) => !previousIds.includes(order.id));
        setOrders(nextOrders);
        previousOrderIdsRef.current = nextOrders.map((order) => order.id);

        if (hasNotifiedNewOrderRef.current && newOrders.length > 0) {
          toast.success(
            newOrders.length === 1
              ? `New order from ${newOrders[0].customer_name}.`
              : `${newOrders.length} new orders received.`
          );
        }

        hasNotifiedNewOrderRef.current = true;
      } catch (error) {
        console.error("Failed to refresh orders from realtime update:", error);
      }
    });

    const productsChannel = productsService.subscribeToProducts(async () => {
      try {
        const nextProducts = await productsService.fetchAdminProducts(activeAccessCode);
        setProducts(nextProducts);
      } catch (error) {
        console.error("Failed to refresh products from realtime update:", error);
      }
    });

    return () => {
      ordersChannel?.unsubscribe();
      productsChannel?.unsubscribe();
    };
  }, [loaded, activeAccessCode]);

  const filteredOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();

    const nextOrders = orders.filter((order) => {
      const matchesStatus =
        orderStatusFilter === "all" ? true : order.payment_status === orderStatusFilter;
      const matchesSearch = query ? buildOrderSearchText(order).includes(query) : true;
      return matchesStatus && matchesSearch;
    });

    nextOrders.sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());

    return nextOrders;
  }, [orders, orderSearch, orderStatusFilter]);

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();

    const nextProducts = products.filter((product) => {
      const matchesSearch = query
        ? [product.name, product.category, product.description]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(query)
        : true;
      const matchesStatus =
        productStatusFilter === "all" ? true : product.stock_status === productStatusFilter;

      return matchesSearch && matchesStatus;
    });

    nextProducts.sort((left, right) => {
      if (productSort === "name_asc") {
        return left.name.localeCompare(right.name);
      }
      if (productSort === "price_asc") {
        return Number(left.price || 0) - Number(right.price || 0);
      }
      if (productSort === "price_desc") {
        return Number(right.price || 0) - Number(left.price || 0);
      }
      return new Date(right.updated_at || right.created_at).getTime()
        - new Date(left.updated_at || left.created_at).getTime();
    });

    return nextProducts;
  }, [products, productSearch, productStatusFilter, productSort]);

  const categoryOptions = useMemo(() => {
    const categorySet = new Set(
      products
        .map((product) => product.category)
        .filter(Boolean)
        .map((category) => category.trim())
    );

    return Array.from(categorySet).sort((left, right) => left.localeCompare(right));
  }, [products]);

  const paginatedOrders = useMemo(() => {
    const start = (orderPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, orderPage]);

  const paginatedProducts = useMemo(() => {
    const start = (productPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, productPage]);

  const orderPageCount = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const productPageCount = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pendingOrderCount = orders.filter((order) => order.payment_status === "pending").length;
  const completedOrderCount = orders.filter((order) => order.payment_status === "paid").length;
  const cancelledOrderCount = orders.filter((order) => order.payment_status === "failed").length;
  const revenueTotal = orders.reduce((sum, order) => {
    if (order.payment_status !== "paid") return sum;
    return sum + Number(order.total_amount || 0);
  }, 0);

  const resetProductForm = () => {
    setProductForm(EMPTY_PRODUCT_FORM);
    setEditingProductId("");
  };

  const handleProductFieldChange = (field) => (event) => {
    const value = field === "featured" ? event.target.checked : event.target.value;
    setProductForm((current) => ({ ...current, [field]: value }));
  };

  const uploadFile = async (file) => {
    setUploadingImage(true);
    try {
      const imageUrl = await uploadImageToCloudinary(file);
      setProductForm((current) => ({ ...current, image_url: imageUrl }));
      toast.success("Image uploaded successfully.");
    } catch (error) {
      console.error("Cloudinary upload failed:", error);
      toast.error(error.message || "Image upload failed.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    event.target.value = "";
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleProductSubmit = async (event) => {
    event.preventDefault();

    if (!activeAccessCode) {
      toast.error("Load the admin dashboard first.");
      return;
    }

    if (!productForm.name.trim() || !productForm.category.trim() || !productForm.price) {
      toast.error("Name, price, and category are required.");
      return;
    }

    const price = Number(productForm.price);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Enter a valid product price.");
      return;
    }

    setSavingProduct(true);

    const payload = {
      name: productForm.name.trim(),
      price,
      category: productForm.category.trim(),
      description: productForm.description.trim(),
      image_url: productForm.image_url.trim(),
      featured: productForm.featured,
      stock_status: productForm.stock_status,
    };

    try {
      const savedProduct = editingProductId
        ? await productsService.updateAdminProduct(activeAccessCode, editingProductId, payload)
        : await productsService.createAdminProduct(activeAccessCode, payload);

      setProducts((current) => {
        if (editingProductId) {
          return current.map((product) => (product.id === savedProduct.id ? savedProduct : product));
        }
        return [savedProduct, ...current];
      });

      toast.success(editingProductId ? "Product updated." : "Product created.");
      resetProductForm();
      setActiveTab("products");
    } catch (error) {
      console.error("Failed to save product:", error);
      toast.error(error.message || "Failed to save product.");
    } finally {
      setSavingProduct(false);
    }
  };

  const startEditingProduct = (product) => {
    setEditingProductId(product.id);
    setProductForm(normalizeProductForm(product));
    setActiveTab("products");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteProduct = async (productId) => {
    if (!activeAccessCode) {
      toast.error("Load the admin dashboard first.");
      return;
    }

    const confirmed = window.confirm("Delete this product from the live catalog?");
    if (!confirmed) return;

    setDeletingProductId(productId);
    try {
      await productsService.deleteAdminProduct(activeAccessCode, productId);
      setProducts((current) => current.filter((product) => product.id !== productId));
      if (editingProductId === productId) {
        resetProductForm();
      }
      toast.success("Product deleted.");
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error(error.message || "Failed to delete product.");
    } finally {
      setDeletingProductId("");
    }
  };

  const renderPagination = (page, pageCount, setPage) => {
    if (pageCount <= 1) return null;

    return (
      <div className="flex flex-wrap items-center justify-center gap-2 pt-6">
        <button
          type="button"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={page === 1}
          className="px-3 py-2 rounded-sm border border-gray-200 bg-white text-sm font-body text-brand-charcoal disabled:opacity-40"
        >
          Previous
        </button>
        {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            onClick={() => setPage(pageNumber)}
            className={`min-w-[40px] px-3 py-2 rounded-sm text-sm font-body transition-colors ${
              pageNumber === page
                ? "bg-brand-red text-white"
                : "border border-gray-200 bg-white text-brand-charcoal hover:text-brand-red"
            }`}
          >
            {pageNumber}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
          disabled={page === pageCount}
          className="px-3 py-2 rounded-sm border border-gray-200 bg-white text-sm font-body text-brand-charcoal disabled:opacity-40"
        >
          Next
        </button>
      </div>
    );
  };

  const productSummaryLabel = `${filteredProducts.length} product${filteredProducts.length === 1 ? "" : "s"}`;
  const orderSummaryLabel = `${filteredOrders.length} order${filteredOrders.length === 1 ? "" : "s"}`;

  return (
    <div className="min-h-screen bg-brand-cream pt-28 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="section-label">Protected View</span>
            <h1 className="font-display text-5xl text-brand-dark font-light mt-2">Admin Dashboard</h1>
            <p className="font-body text-gray-500 mt-3 max-w-2xl">
              A cleaner control room for orders and products, built to keep updates fast, clear,
              and comfortably manageable across desktop and mobile.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadDashboard()}
            disabled={loading || !loaded}
            className="inline-flex items-center justify-center gap-2 self-start sm:self-auto px-4 py-3 rounded-sm bg-white text-brand-charcoal font-body font-semibold text-sm shadow-sm border border-gray-100 hover:text-brand-red transition-colors disabled:opacity-50"
          >
            <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
            Refresh Dashboard
          </button>
        </div>

        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-sm bg-white p-5 shadow-sm">
            <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Pending</p>
            <p className="mt-3 font-display text-3xl text-brand-dark">{pendingOrderCount}</p>
          </div>
          <div className="rounded-sm bg-white p-5 shadow-sm">
            <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Completed</p>
            <p className="mt-3 font-display text-3xl text-brand-dark">{completedOrderCount}</p>
          </div>
          <div className="rounded-sm bg-white p-5 shadow-sm">
            <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Cancelled</p>
            <p className="mt-3 font-display text-3xl text-brand-dark">{cancelledOrderCount}</p>
          </div>
          <div className="rounded-sm bg-white p-5 shadow-sm">
            <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Revenue</p>
            <p className="mt-3 font-display text-3xl text-brand-red">{formatPrice(revenueTotal)}</p>
          </div>
        </div>

        <div className="grid gap-8 xl:grid-cols-[360px_1fr] items-start">
          <div className="space-y-6">
            <form onSubmit={loadDashboard} className="bg-white p-8 rounded-sm shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <ShieldCheck size={16} className="text-brand-red" />
                <h2 className="font-display text-2xl text-brand-dark">Access Dashboard</h2>
              </div>

              <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                Admin Access Code
              </label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showAccessCode ? "text" : "password"}
                  value={accessCode}
                  onChange={(event) => setAccessCode(event.target.value)}
                  placeholder="Enter secure code"
                  className="input-field pl-10 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowAccessCode((current) => !current)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-brand-red"
                  aria-label={showAccessCode ? "Hide admin access code" : "Show admin access code"}
                >
                  {showAccessCode ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="py-3 bg-brand-red text-white font-body font-semibold tracking-wide text-sm rounded-sm hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <RefreshCcw size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                  {loading ? "Loading" : "Unlock"}
                </button>
                <button
                  type="button"
                  onClick={() => loadDashboard()}
                  disabled={loading || !loaded}
                  className="py-3 bg-brand-cream text-brand-charcoal font-body font-semibold tracking-wide text-sm rounded-sm hover:text-brand-red transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <RefreshCcw size={14} />
                  Refresh
                </button>
              </div>
            </form>

            <form onSubmit={handleProductSubmit} className="bg-white p-8 rounded-sm shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-6">
                <div>
                  <p className="font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    Product Studio
                  </p>
                  <h2 className="font-display text-2xl text-brand-dark">
                    {editingProductId ? "Edit Product" : "Add Product"}
                  </h2>
                </div>
                {editingProductId && (
                  <button
                    type="button"
                    onClick={resetProductForm}
                    className="font-body text-xs uppercase tracking-widest text-brand-red hover:text-red-700 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={handleProductFieldChange("name")}
                    className="input-field"
                    placeholder="Product name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                      Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.price}
                      onChange={handleProductFieldChange("price")}
                      className="input-field"
                      placeholder="50000"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                      Category
                    </label>
                    <select
                      value={productForm.category}
                      onChange={handleProductFieldChange("category")}
                      className="input-field"
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={productForm.description}
                    onChange={handleProductFieldChange("description")}
                    className="input-field resize-none"
                    placeholder="Describe the fragrance"
                  />
                </div>

                <div>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    Image
                  </label>
                  <input
                    type="url"
                    value={productForm.image_url}
                    onChange={handleProductFieldChange("image_url")}
                    className="input-field"
                    placeholder="https://..."
                  />

                  <div
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`mt-3 rounded-sm border border-dashed p-4 transition-colors ${
                      isDragOver ? "border-brand-red bg-red-50/40" : "border-gray-200 bg-brand-cream/60"
                    }`}
                  >
                    <label className="flex flex-col items-center justify-center gap-2 text-center cursor-pointer">
                      <UploadCloud size={20} className="text-brand-red" />
                      <p className="font-body text-sm text-brand-charcoal">
                        {uploadingImage ? "Uploading image..." : "Drag and drop or click to upload to Cloudinary"}
                      </p>
                      <p className="font-body text-xs text-gray-400">JPG, PNG, WEBP</p>
                      <span className="inline-flex items-center gap-2 text-sm font-body text-brand-red hover:text-red-700 transition-colors">
                        <ImagePlus size={14} />
                        Choose image
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                      Stock Status
                    </label>
                    <select
                      value={productForm.stock_status}
                      onChange={handleProductFieldChange("stock_status")}
                      className="input-field"
                    >
                      {STOCK_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-3 pt-8">
                    <input
                      type="checkbox"
                      checked={productForm.featured}
                      onChange={handleProductFieldChange("featured")}
                      className="h-4 w-4 accent-brand-red"
                    />
                    <span className="font-body text-sm text-brand-charcoal">Show as featured</span>
                  </label>
                </div>

                {productForm.image_url && (
                  <div className="rounded-sm overflow-hidden border border-gray-200 bg-brand-cream p-3">
                    <img
                      src={optimizeImageUrl(productForm.image_url, { width: 900 })}
                      alt="Product preview"
                      className="w-full h-48 object-cover rounded-sm"
                      loading="eager"
                      fetchPriority="high"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={savingProduct || uploadingImage}
                  className="py-3 bg-brand-red text-white font-body font-semibold tracking-wide text-sm rounded-sm hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingProduct ? <RefreshCcw size={14} className="animate-spin" /> : editingProductId ? <Save size={14} /> : <PlusCircle size={14} />}
                  {savingProduct ? "Saving" : editingProductId ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={resetProductForm}
                  className="py-3 bg-brand-cream text-brand-charcoal font-body font-semibold tracking-wide text-sm rounded-sm hover:text-brand-red transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <RefreshCcw size={14} />
                  Clear Form
                </button>
              </div>
            </form>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-sm shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-6 py-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab("orders")}
                    className={`px-4 py-2 rounded-sm font-body text-sm transition-colors ${
                      activeTab === "orders"
                        ? "bg-brand-red text-white"
                        : "bg-brand-cream text-brand-charcoal hover:text-brand-red"
                    }`}
                  >
                    Orders
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("products")}
                    className={`px-4 py-2 rounded-sm font-body text-sm transition-colors ${
                      activeTab === "products"
                        ? "bg-brand-red text-white"
                        : "bg-brand-cream text-brand-charcoal hover:text-brand-red"
                    }`}
                  >
                    Products
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {activeTab !== "orders" && (
                    <div className="px-3 py-2 rounded-sm bg-brand-cream text-brand-charcoal text-sm font-body">
                      {productSummaryLabel}
                    </div>
                  )}
                </div>
              </div>

              {activeTab === "orders" ? (
                <div className="p-6">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
                    <div className="relative">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={orderSearch}
                        onChange={(event) => setOrderSearch(event.target.value)}
                        className="input-field pl-10"
                        placeholder="Search customer, reference, email, item..."
                      />
                    </div>
                    <select
                      value={orderStatusFilter}
                      onChange={(event) => setOrderStatusFilter(event.target.value)}
                      className="input-field"
                    >
                      {ORDER_STATUS_FILTERS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                        ))}
                      </select>
                  </div>

                  <div className="mt-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-body text-sm text-gray-400">{orderSummaryLabel}</p>
                      <button
                        type="button"
                        onClick={() => loadDashboard()}
                        disabled={loading || !loaded}
                        className="inline-flex items-center gap-2 rounded-sm bg-brand-cream px-3 py-2 text-sm font-body text-brand-charcoal transition-colors hover:text-brand-red disabled:opacity-50"
                      >
                        <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                        Refresh Data
                      </button>
                    </div>
                  </div>

                  {!loaded ? (
                    <div className="px-4 py-20 text-center">
                      <ShoppingBag size={52} className="mx-auto text-gray-200 mb-4" />
                      <p className="font-display text-2xl text-gray-300">Orders will appear here</p>
                      <p className="font-body text-sm text-gray-400 mt-2">
                        Unlock the dashboard with your admin access code.
                      </p>
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <div className="px-4 py-20 text-center">
                      <p className="font-display text-2xl text-gray-300">No orders match your filters</p>
                      <p className="font-body text-sm text-gray-400 mt-2">
                        Try a different search or status view.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mt-6 space-y-4">
                        {paginatedOrders.map((order) => (
                          <article key={order.id} className="rounded-sm border border-gray-100 bg-brand-cream/30 p-5">
                            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="font-display text-2xl text-brand-dark">{order.customer_name}</h3>
                                  <span
                                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest rounded-sm font-body ${
                                      ORDER_BADGE_CLASSES[order.payment_status] || ORDER_BADGE_CLASSES.pending
                                    }`}
                                  >
                                    {getOrderLabel(order.payment_status)}
                                  </span>
                                </div>
                                <p className="font-body text-sm text-gray-500 mt-2">{order.email}</p>
                                <p className="font-body text-sm text-gray-500">{order.phone || "No phone provided"}</p>
                                <p className="font-body text-xs uppercase tracking-widest text-gray-400 mt-3">
                                  Ref {order.payment_reference}
                                </p>
                              </div>

                              <div className="text-left xl:text-right">
                                <p className="font-display text-2xl text-brand-red">{formatPrice(order.total_amount)}</p>
                                <p className="font-body text-sm text-gray-500 mt-2">
                                  {formatDate(order.paid_at || order.created_at)}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedOrder(order)}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-white border border-gray-200 text-brand-charcoal text-sm font-body hover:text-brand-red"
                              >
                                <Eye size={14} />
                                Quick View
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                      {renderPagination(orderPage, orderPageCount, setOrderPage)}
                    </>
                  )}
                </div>
              ) : (
                <div className="p-6">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
                    <div className="relative">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(event) => setProductSearch(event.target.value)}
                        className="input-field pl-10"
                        placeholder="Search product name, category, description..."
                      />
                    </div>
                    <select
                      value={productStatusFilter}
                      onChange={(event) => setProductStatusFilter(event.target.value)}
                      className="input-field"
                    >
                      <option value="all">All Stock</option>
                      {STOCK_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={productSort}
                      onChange={(event) => setProductSort(event.target.value)}
                      className="input-field"
                    >
                      {PRODUCT_SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5">
                    <p className="font-body text-sm text-gray-400">{productSummaryLabel}</p>
                  </div>

                  {!loaded ? (
                    <div className="px-4 py-20 text-center">
                      <Package size={52} className="mx-auto text-gray-200 mb-4" />
                      <p className="font-display text-2xl text-gray-300">Products will appear here</p>
                      <p className="font-body text-sm text-gray-400 mt-2">
                        Unlock the dashboard to manage your catalog.
                      </p>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="px-4 py-20 text-center">
                      <p className="font-display text-2xl text-gray-300">No products match your filters</p>
                      <p className="font-body text-sm text-gray-400 mt-2">
                        Try a different search or stock view.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mt-6 grid sm:grid-cols-2 2xl:grid-cols-3 gap-4">
                        {paginatedProducts.map((product) => (
                          <article
                            key={product.id}
                            className="rounded-sm border overflow-hidden bg-white shadow-sm transition-colors border-gray-100"
                          >
                            <div className="relative bg-brand-cream aspect-[4/3]">
                              {product.image_url ? (
                                <img
                                  src={optimizeImageUrl(product.image_url, { width: 900 })}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  loading="eager"
                                  fetchPriority="high"
                                  sizes="(min-width: 1536px) 20vw, (min-width: 640px) 40vw, 100vw"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300 font-body text-sm">
                                  No image
                                </div>
                              )}
                              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                                <span
                                  className={`px-2.5 py-1 font-body text-[10px] uppercase tracking-widest rounded-sm ${
                                    STOCK_BADGE_CLASSES[product.stock_status] || STOCK_BADGE_CLASSES.in_stock
                                  }`}
                                >
                                  {getStockLabel(product.stock_status)}
                                </span>
                                {product.featured && (
                                  <span className="px-2.5 py-1 bg-brand-red text-white font-body text-[10px] uppercase tracking-widest rounded-sm">
                                    Featured
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="font-display text-xl text-brand-dark">{product.name}</h3>
                                  <p className="font-body text-xs uppercase tracking-widest text-gray-400 mt-1">
                                    {product.category}
                                  </p>
                                </div>
                                <p className="font-display text-xl text-brand-red">{formatPrice(product.price)}</p>
                              </div>

                              <p className="font-body text-sm text-gray-500 leading-relaxed mt-3 min-h-[3.75rem]">
                                {product.description || "No description added yet."}
                              </p>

                              <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                                <span>Updated {formatDate(product.updated_at || product.created_at)}</span>
                                <span className="inline-flex items-center gap-1">
                                  <ArrowDownUp size={12} />
                                  Live
                                </span>
                              </div>

                              <div className="mt-4 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEditingProduct(product)}
                                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-brand-cream text-brand-charcoal font-body text-sm rounded-sm hover:text-brand-red transition-colors"
                                >
                                  <Pencil size={14} />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteProduct(product.id)}
                                  disabled={deletingProductId === product.id}
                                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-red-50 text-red-700 font-body text-sm rounded-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                  {deletingProductId === product.id ? (
                                    <RefreshCcw size={14} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={14} />
                                  )}
                                </button>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                      {renderPagination(productPage, productPageCount, setProductPage)}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-brand-dark/60 px-4 py-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-sm shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-6">
              <div>
                <p className="font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">Order Detail</p>
                <h2 className="font-display text-3xl text-brand-dark">{selectedOrder.customer_name}</h2>
                <p className="font-body text-sm text-gray-500 mt-2">{selectedOrder.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-sm bg-brand-cream text-brand-charcoal hover:text-brand-red"
              >
                <XCircle size={18} />
              </button>
            </div>

            <div className="p-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <h3 className="font-display text-xl text-brand-dark mb-4">Items</h3>
                <div className="space-y-3">
                  {(selectedOrder.items || []).map((item, index) => (
                    <div
                      key={`${selectedOrder.id}-${item.id || item.name}-${index}`}
                      className="rounded-sm bg-brand-cream/50 px-4 py-3 flex items-center justify-between gap-4"
                    >
                      <div>
                        <p className="font-body font-medium text-sm text-brand-charcoal">{item.name}</p>
                        <p className="font-body text-xs text-gray-400">
                          Qty {item.quantity}
                          {item.category ? ` - ${item.category}` : ""}
                        </p>
                      </div>
                      <p className="font-body text-sm font-semibold text-brand-charcoal">
                        {formatPrice((item.price || 0) * (item.quantity || 1))}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-sm border border-gray-100 p-4">
                  <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Payment Status</p>
                  <div className="mt-3">
                    <span
                      className={`inline-flex px-3 py-2 text-xs uppercase tracking-widest rounded-sm font-body ${
                        ORDER_BADGE_CLASSES[selectedOrder.payment_status] || ORDER_BADGE_CLASSES.pending
                      }`}
                    >
                      {getOrderLabel(selectedOrder.payment_status)}
                    </span>
                  </div>
                </div>

                <div className="rounded-sm border border-gray-100 p-4 space-y-3">
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Reference</p>
                    <p className="font-body text-sm text-brand-charcoal mt-1">{selectedOrder.payment_reference}</p>
                  </div>
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Phone</p>
                    <p className="font-body text-sm text-brand-charcoal mt-1">
                      {selectedOrder.phone || "No phone provided"}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Address</p>
                    <p className="font-body text-sm text-brand-charcoal mt-1">{selectedOrder.address}</p>
                  </div>
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Paid At</p>
                    <p className="font-body text-sm text-brand-charcoal mt-1">
                      {formatDate(selectedOrder.paid_at || selectedOrder.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-gray-400">Total</p>
                    <p className="font-display text-3xl text-brand-red mt-1">
                      {formatPrice(selectedOrder.total_amount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
